/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useRef } from 'react';

import { IServerSideGetRowsParams } from 'ag-grid-community';
import { set } from 'lodash';

import { useAppSelector } from '@/app/hooks';
import type { GridTotalStore } from '@/components';
import { DonorSchedule } from '@/rtk-query.gql';
import { getDateFilter } from '@/utils/getDateFilter';
import { getOperator } from '@/utils/getOperator';

export function useRTKQueryDataSource({
  query,
  queryName,
  variables,
  semenId,
  skip,
  quickFilterText,
}: {
  query: any;
  queryName: string;
  variables?: any;
  semenId?: string;
  skip?: boolean;
  quickFilterText?: string;
}) {
  const apiRef = useRef<{ api: any } | null>(null);
  const { refetchTrigger } = useAppSelector((state) => state.donorScheduling);

  const fieldMappings: { [key: string]: string } = {
    donor: 'donor.name',
    semenTypeValue: 'semenType',
    owner: 'owner.code',
    implantTypeValue: 'implantType',
    soScheduleId: 'soSchedule.description',
  };

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        return;
      }

      apiRef.current = { api };

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field:
            fieldMappings[s.colId] || api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) => set({}, s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'));

      const filterModel = request.filterModel;
      const filterConditions: any[] = [];

      Object.entries(filterModel || {}).forEach(([field, condition]) => {
        const [parent, child, grandchild] = field.split('.');

        if (field === 'date') {
          filterConditions.push(getDateFilter('date', condition));
          return;
        }

        if (child === 'breed' && condition.filterType === 'set') {
          filterConditions.push({
            or: condition.values.map((v: string) => ({
              [parent]: { breed: { [grandchild ?? 'subFamily']: { eq: v } } },
            })),
          });
          return;
        }

        if (parent === 'breed') {
          const isSetFilter = condition.filterType === 'set';
          if (child === 'subFamily' && isSetFilter) {
            filterConditions.push({
              or: condition.values.map((v: string) => ({
                breed: { subFamily: { eq: v } },
              })),
            });
          } else {
            filterConditions.push({
              breed: {
                [child ?? 'code']: {
                  [getOperator(condition.type)]: condition.filter,
                },
              },
            });
          }
          return;
        }

        if (condition.filterType === 'set' && condition.values) {
          const booleanFields = new Set(['pool', 'tt']);
          const isBool = booleanFields.has(field);

          if (isBool) {
            const boolValues: boolean[] = condition.values.map(
              (v: any) => v === 'true',
            );
            if (boolValues.length === 1) {
              filterConditions.push(
                child
                  ? { [parent]: { [child]: { eq: boolValues[0] } } }
                  : { [field]: { eq: boolValues[0] } },
              );
            }
            return;
          }

          const integerValues = condition.values.map((value: any) =>
            value == null ? null : parseInt(value, 10),
          );
          filterConditions.push(
            child
              ? { [parent]: { [child]: { in: integerValues } } }
              : { [field]: { in: integerValues } },
          );
          return;
        }

        if (field === 'donor.yard.name') {
          if (condition.operator && condition.conditions) {
            filterConditions.push({
              [condition.operator.toLowerCase()]: condition.conditions.map(
                (cond: any) => ({
                  donor: {
                    yard: {
                      name: { [getOperator(cond.type)]: cond.filter },
                    },
                  },
                }),
              ),
            });
          } else {
            filterConditions.push({
              donor: {
                yard: {
                  name: { [getOperator(condition.type)]: condition.filter },
                },
              },
            });
          }
          return;
        }

        if (parent === 'donor' || parent === 'sire') {
          if (condition.operator && condition.conditions) {
            filterConditions.push({
              [condition.operator.toLowerCase()]: condition.conditions.map(
                (cond: any) => ({
                  [parent]: {
                    name: { [getOperator(cond.type)]: cond.filter },
                  },
                }),
              ),
            });
          } else {
            filterConditions.push({
              [parent]: {
                name: { [getOperator(condition.type)]: condition.filter },
              },
            });
          }
          return;
        }

        if (parent === 'owner') {
          if (child === 'code') {
            filterConditions.push({
              owner: {
                code: { [getOperator(condition.type)]: condition.filter },
              },
            });
          } else {
            filterConditions.push({
              owner: {
                name: { [getOperator(condition.type)]: condition.filter },
              },
            });
          }
          return;
        }

        filterConditions.push(
          child
            ? {
                [parent]: {
                  [child]: {
                    [getOperator(condition.type)]: condition.filter,
                  },
                },
              }
            : {
                [field]: {
                  [getOperator(condition.type)]: condition.filter,
                },
              },
        );
      });

      const allConditions: any[] = [];

      filterConditions.forEach((condition) => {
        if (condition.and) {
          allConditions.push(...condition.and);
        } else {
          allConditions.push(condition);
        }
      });

      if (semenId) {
        allConditions.push({
          semenId: { eq: semenId },
        });
      }

      if (quickFilterText) {
        allConditions.push({
          or: [
            { csr: { fullName: { contains: quickFilterText } } },
            {
              fertilizationSite: { name: { contains: quickFilterText } },
            },
            {
              aspirationSite: { name: { contains: quickFilterText } },
            },
            { dow: { contains: quickFilterText } },
            { donor: { name: { contains: quickFilterText } } },
            { sire: { name: { contains: quickFilterText } } },
            { backupSire: { name: { contains: quickFilterText } } },
            { owner: { name: { contains: quickFilterText } } },
            { owner: { code: { contains: quickFilterText } } },
            {
              soSchedule: { description: { contains: quickFilterText } },
            },
          ],
        });
      }

      const where =
        allConditions.length > 0 ? { and: allConditions } : undefined;

      query({
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order,
        ...(where && { where }),
        ...variables,
      })
        .unwrap()
        .then((res: any) => {
          const queryResult = res[queryName] as {
            items: DonorSchedule[];
            totalCount: number;
          };
          success({
            rowData: queryResult.items,
            rowCount: queryResult.totalCount,
          });
        })
        .catch(fail);
    },
    [
      query,
      queryName,
      quickFilterText,
      skip,
      variables,
      refetchTrigger,
      semenId,
    ],
  );

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch: () => {
        if (apiRef.current?.api) {
          apiRef.current.api.refreshServerSide({ purge: true });
        }
      },
    }),
    [getRows],
  );
}

export function useRestDataSource({
  query,
  semenId,
  skip,
  quickFilterText,
  totalStore,
}: {
  query: (params: {
    request: IServerSideGetRowsParams['request'];
    quickFilterText?: string;
    semenId?: string;
  }) => { unwrap: () => Promise<any> };
  semenId?: string;
  skip?: boolean;
  quickFilterText?: string;
  totalStore?: GridTotalStore;
}) {
  const apiRef = useRef<{ api: any } | null>(null);
  const queryGeneration = useRef(0);
  const { refetchTrigger } = useAppSelector((state) => state.donorScheduling);

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        return;
      }

      apiRef.current = { api };

      const startRow = request.startRow ?? 0;
      if (startRow === 0) {
        queryGeneration.current += 1;
        if (totalStore?.get() !== undefined) {
          totalStore?.set(null);
        }
      }
      const generation = queryGeneration.current;

      query({
        request,
        quickFilterText,
        semenId,
      })
        .unwrap()
        .then((res) => {
          const queryResult = res.donorSchedules as {
            items: DonorSchedule[];
            hasMore: boolean;
            totalCount?: number | null;
            totalCountCapped?: boolean;
          };
          if (totalStore && generation === queryGeneration.current) {
            if (!queryResult.hasMore) {
              totalStore.set(startRow + queryResult.items.length);
            } else if (startRow === 0) {
              totalStore.set(
                queryResult.totalCount == null
                  ? undefined
                  : {
                      count: queryResult.totalCount,
                      capped: queryResult.totalCountCapped ?? false,
                    },
              );
            }
          }
          success({
            rowData: queryResult.items,
            rowCount: queryResult.hasMore
              ? undefined
              : startRow + queryResult.items.length,
          });
        })
        .catch(() => {
          if (startRow === 0 && generation === queryGeneration.current) {
            totalStore?.set(undefined);
          }
          fail();
        });
    },
    [query, quickFilterText, skip, refetchTrigger, semenId, totalStore],
  );

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch: () => {
        if (apiRef.current?.api) {
          apiRef.current.api.refreshServerSide({ purge: true });
        }
      },
    }),
    [getRows],
  );
}
